import { LoadingAnimation } from '@outbound-ai/ursa'
import { useEffect, useRef, useState } from 'react'
import { FaPause, FaPlay } from 'react-icons/fa'
import { Button } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { currentPercentage, audioTimeToMMSS } from 'utils/audio'

interface IProps {
  audioSrc: string
  isLoadingPlayAudio: boolean
}

export const PostCallAudioPlayer = ({ audioSrc, isLoadingPlayAudio }: IProps) => {
  const [trackProgress, setTrackProgress] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)

  // Refs
  const audioRef = useRef(new Audio(audioSrc))
  const intervalRef = useRef<NodeJS.Timeout | null>()

  useEffect(() => {
    if (isPlaying) {
      audioRef.current.play()
      startTimer()
    } else {
      audioRef.current.pause()
    }
  }, [isPlaying])

  useEffect(() => {
    // Pause and clean up on unmount
    return () => {
      audioRef.current.pause()
      intervalRef.current && clearInterval(intervalRef.current)
    }
  }, [])

  const handlePlay = () => {
    setIsPlaying(true)
  }

  const handlePause = () => {
    setIsPlaying(false)
  }

  const startTimer = () => {
    // Clear any timers already running
    intervalRef.current && clearInterval(intervalRef.current)

    intervalRef.current = setInterval(() => {
      setTrackProgress(audioRef.current.currentTime)
    }, 1000)
  }

  const onScrub = (value: string) => {
    // Clear any timers already running
    intervalRef.current && clearInterval(intervalRef.current)

    audioRef.current.currentTime = +value
    setTrackProgress(audioRef.current.currentTime)
  }

  const onScrubEnd = () => {
    // If not already playing, start
    if (!isPlaying) {
      setIsPlaying(true)
    }
    startTimer()
  }

  // variable
  const { duration, currentTime } = audioRef.current

  return (
    <Div currentPercentage={currentPercentage(trackProgress, duration)}>
      <div className="actions">
        {isPlaying ? (
          <Button data-testid="playButton" onClick={handlePause}>
            <FaPause />
          </Button>
        ) : (
          <Button data-testid="pauseButton" onClick={handlePlay}>
            {isLoadingPlayAudio ? <LoadingAnimation /> : <FaPlay />}
          </Button>
        )}
      </div>
      <div className="audio-progress">
        <input
          type="range"
          value={trackProgress}
          step="1"
          min="0"
          max={duration ? duration : `${duration}`}
          className="audio-progress-input"
          onChange={(e) => onScrub(e.target.value)}
          onMouseUp={onScrubEnd}
          onKeyUp={onScrubEnd}
        />
        {!isNaN(duration) && (
          <div className="audio-duration">
            {audioTimeToMMSS(currentTime)} / {audioTimeToMMSS(duration)}
          </div>
        )}
      </div>
    </Div>
  )
}

const Div = styled.div<{ currentPercentage: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-grow: 1;

  > .actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    button {
      background-color: ${({ theme }) => theme.colors.secondary.darker};
      color: ${({ theme }) => theme.colors.grey.lightest};
    }
  }

  .audio-progress {
    display: flex;
    flex-grow: 1;
    position: relative;

    .audio-duration {
      white-space: nowrap;
      ${({ theme }) => theme.typography.xs.normal};
      margin-left: ${({ theme }) => theme.util.pixelToRem(12)};
    }

    > input[type='range'] {
      align-self: center;
      height: ${({ theme }) => theme.util.pixelToRem(8)};
      -webkit-appearance: none;
      width: 100%;
      border-radius: ${({ theme }) => theme.util.pixelToRem(20)};
      transition: background 0.2s ease;
      cursor: pointer;

      background: -webkit-gradient(
        linear,
        0% 0%,
        100% 0%,
        color-stop(${({ currentPercentage }) => currentPercentage}, ${({ theme }) => theme.colors.info.darker}),
        color-stop(${({ currentPercentage }) => currentPercentage}, ${({ theme }) => theme.colors.primary.lighter})
      );

      &::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 1.125rem;
        height: 1.125rem;
        border-radius: 10rem;
        background: ${({ theme }) => theme.colors.info.darker};
        box-shadow: ${({ theme }) => theme.shadows.normal};
        cursor: -moz-grab;
        cursor: -webkit-grab;
      }
    }
  }
`
